import { mergeAttributes, Node } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { isTruthy } from '@_/utils';
import { getFileFromObjectUrl, getImageDimensions, isObjectUrl } from '@_/utils-frontend';
import { EditableDocumentImage } from './EditableDocumentImage.tsx';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    documentImage: {
      insertImage: (attributes: { file?: File; src: string }) => ReturnType;
    };
  }
}

export interface DocumentImageAttributes {
  dimensions?: { width: number; height: number };
  file?: File;
  imageId?: string;
  src?: string;
  uploadedFileName?: string;
}

const EXT_NAME = 'documentImage';

export const DocumentImage = Node.create({
  name: EXT_NAME,

  draggable: true,
  group: 'block',
  inline: false,

  addNodeView() {
    return ReactNodeViewRenderer(EditableDocumentImage);
  },

  addAttributes() {
    return {
      dimensions: {
        default: undefined,
        parseHTML: element => {
          const [width, height] =
            element
              .getAttribute('data-dimensions')
              ?.split(',')
              .map(str => parseInt(str, 10)) || [];

          return !isNaN(width) && !isNaN(height) ? { width, height } : undefined;
        },
        renderHTML: attributes => {
          return {
            'data-dimensions': attributes.dimensions
              ? `${attributes.dimensions.width},${attributes.dimensions.height}`
              : undefined
          };
        }
      },
      file: {
        default: undefined,
        rendered: false
      },
      imageId: {
        default: undefined,
        parseHTML: element => element.getAttribute('data-image-id'),
        renderHTML: attributes => {
          return { 'data-image-id': attributes.imageId || '' };
        }
      },
      src: {
        default: undefined,
        parseHTML: element => {
          return element.getAttribute('src') || element.getAttribute('data-src');
        },
        renderHTML: attributes => {
          return { 'data-src': attributes.src || undefined };
        }
      },
      uploadedFileName: {
        default: undefined,
        rendered: false
      }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'img',
        getAttrs: element => {
          const imageId = element.getAttribute('data-image-id');
          const src = element.getAttribute('src') || element.getAttribute('data-src');
          if (!src || (!imageId && !isObjectUrl(src))) {
            return false;
          }
          return null;
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes, { class: 'mx-auto' })];
  },

  async onUpdate() {
    const { state, view } = this.editor;
    const { tr } = state;

    const imagePositions = this.editor.$nodes(EXT_NAME);

    if (!imagePositions?.length) {
      return;
    }

    for (const { node, pos } of imagePositions) {
      const { dimensions, file, imageId, src } = node.attrs;

      // Remove any image nodes that do not have a valid image ID or file
      if (!imageId && !file) {
        // Since non-serializable attributes are not retained during copy-paste,
        // we need to retrieve the file via its object URL
        const fetchedFile = await getFileFromObjectUrl(src);

        if (fetchedFile) {
          // Update node with the fetched file
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            file: fetchedFile
          });
        }

        if (!fetchedFile || !dimensions) {
          // Remove node if the file is not valid
          tr.delete(pos, pos + node.nodeSize);
        }
      }

      // Update newly created nodes with image dimensions
      if (!imageId && !dimensions) {
        const { width, height } = (await getImageDimensions(src)) || {};
        if (width && height) {
          tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            dimensions: { width, height }
          });
        }
      }

      // TODO: Validate image file's MIME type
    }

    if (tr.steps.length) {
      view.dispatch(tr);
    }
  },

  addCommands() {
    return {
      insertImage:
        ({ file, src }) =>
        ({ commands }) => {
          return commands.insertContent({
            attrs: { file, src },
            type: this.name
          });
        }
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(EXT_NAME),
        props: {
          handlePaste: (view, event) => {
            const { clipboardData } = event;
            if (!clipboardData) {
              return false;
            }

            // Check if the clipboard contains any image files
            const items = Array.from(clipboardData.items);
            const files = items
              .filter(item => item.type.startsWith('image/'))
              .map(item => item.getAsFile())
              .filter(isTruthy);

            if (!files.length) {
              return false;
            }

            const objectUrl = URL.createObjectURL(files[0]);

            // Create image node with object URL
            view.dispatch(
              view.state.tr.replaceSelectionWith(
                this.editor.schema.nodes[EXT_NAME].create({
                  file: files[0],
                  src: objectUrl
                })
              )
            );

            event.preventDefault();
            return true;
          },

          handleDrop: (view, event) => {
            const { dataTransfer } = event;
            if (!dataTransfer) {
              return false;
            }

            // Check if image files were dropped
            const files = Array.from(dataTransfer.files).filter(file =>
              file.type.startsWith('image/')
            );

            if (!files.length) {
              return false;
            }

            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY
            });

            if (!coordinates) {
              return;
            }

            const objectUrl = URL.createObjectURL(files[0]);

            // Create image node with object URL
            view.dispatch(
              view.state.tr.insert(
                coordinates.pos,
                this.editor.schema.nodes[EXT_NAME].create({
                  file: files[0],
                  src: objectUrl
                })
              )
            );

            event.preventDefault();
            return true;
          }
        }
      })
    ];
  }
});
